import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { AIOwnerBrief } from '@/types/ai'

// ── The owner's daily brief, shared by the dashboard and the copilot bubble ──
// One cache entry per signed-in admin (`['ai','owner-brief',userId]`), so the
// dashboard panel and the bubble's card show the same brief and a dismiss in
// one place hides it in the other. The endpoint answers null for non-admins,
// the marketing edition, and a tenant with no closed day, so `enabled` only
// saves the call for roles the server would refuse anyway.

export const ownerBriefQueryKey = (userId: string) => ['ai', 'owner-brief', userId] as const

export interface OwnerBriefState {
  brief: AIOwnerBrief | null
  isLoading: boolean
  error: unknown
  /** Marks the brief seen: optimistic in the cache, best-effort on the server. */
  markSeen: (id: string) => void
}

export function useOwnerBrief(userId: string, role: string): OwnerBriefState {
  const queryClient = useQueryClient()
  const key = useMemo(() => ownerBriefQueryKey(userId), [userId])
  const query = useQuery({
    queryKey: key,
    queryFn: () => apiClient.aiGetOwnerBrief(),
    enabled: role === 'admin',
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const markSeen = useCallback(
    (id: string) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof apiClient.aiGetOwnerBrief>>>(key, (prev) =>
        prev?.data && prev.data.id === id ? { ...prev, data: { ...prev.data, seen: true } } : prev,
      )
      apiClient.aiMarkOwnerBriefSeen(id).catch(() => {})
    },
    [queryClient, key],
  )

  return {
    brief: query.data?.data ?? null,
    isLoading: role === 'admin' && query.isLoading,
    error: query.error,
    markSeen,
  }
}
